import { get, post } from "../../lib/api";
import { verfolgeStrom } from "./strom";
import type {
  LaufStatus,
  LaufZeile,
  OffeneFreigabe,
  RohEreignis,
  WerkzeugEintrag,
  Zug,
} from "./typen";

/**
 * The panel's controller.
 *
 * State and the streaming lifecycle live here, outside React, for one reason: the
 * shell unmounts the panel when it is closed. A run held in component state would be
 * abandoned mid-flight the moment a Verwalter collapsed the sidebar to look at a grid —
 * which is exactly when a long run is most useful. Here, closing the panel closes a
 * view; the run keeps arriving, and reopening shows it where it got to.
 *
 * The store is deliberately plain. `useSyncExternalStore` needs a stable snapshot and
 * nothing more, and a reducer library would add a vocabulary without adding a
 * guarantee.
 */

export interface FreigabeZustand {
  laeuft: boolean;
  entschieden: "erteilt" | "verweigert" | null;
  fehler: string | null;
}

export interface Sitzung {
  konversationId: string | null;
  zuege: Zug[];
  /** Open approval requests, indexed by id, hydrated when a run stops at a gate. */
  freigaben: Record<string, OffeneFreigabe>;
  freigabeZustand: Record<string, FreigabeZustand>;
  katalog: Map<string, WerkzeugEintrag>;
  /** True while the POST that enqueues a run is in flight. */
  sendet: boolean;
}

const LEER: Sitzung = {
  konversationId: null,
  zuege: [],
  freigaben: {},
  freigabeZustand: {},
  katalog: new Map(),
  sendet: false,
};

let zustand: Sitzung = LEER;
const hoerer = new Set<() => void>();

export function abonniere(rueckruf: () => void): () => void {
  hoerer.add(rueckruf);
  return () => hoerer.delete(rueckruf);
}

export function lies(): Sitzung {
  return zustand;
}

function setze(aenderung: (alt: Sitzung) => Sitzung): void {
  const vorher = zustand;
  zustand = aenderung(zustand);
  for (const rueckruf of hoerer) rueckruf();
  // Only the identity of the conversation is persisted, so writing on every state
  // change is cheap and there is no path where the store and the tab disagree.
  if (zustand.zuege !== vorher.zuege || zustand.konversationId !== vorher.konversationId) {
    lege_ab();
  }
}

function aendereZug(zugId: string, aenderung: (zug: Zug) => Zug): void {
  setze((alt) => ({
    ...alt,
    zuege: alt.zuege.map((z) => (z.id === zugId ? aenderung(z) : z)),
  }));
}

// ── Wiederaufnahme nach einem Neuladen ──────────────────────────────────────

const ABLAGE = "kontor.assistent.sitzung";

interface AbgelegterZug {
  id: string;
  frage: string;
  runId: string | null;
  kontextLabel: string | null;
}

/**
 * What survives a reload.
 *
 * Only the question and the run id — never the transcript. The events, the cost and
 * the final text are re-read from the run row, so a restored conversation shows what
 * the database says happened rather than a stale copy of what this tab last saw. A
 * run that was still working when the tab reloaded is picked up again mid-flight.
 *
 * `sessionStorage`, not `localStorage`: a run belongs to the tab that started it, and
 * a Verwalter opening a second window should not find someone else's half-finished
 * question in it.
 */
function lege_ab(): void {
  try {
    const zuege: AbgelegterZug[] = zustand.zuege.slice(-10).map((z) => ({
      id: z.id,
      frage: z.frage,
      runId: z.runId,
      kontextLabel: z.kontextLabel,
    }));
    sessionStorage.setItem(
      ABLAGE,
      JSON.stringify({ konversationId: zustand.konversationId, zuege }),
    );
  } catch {
    // A full or disabled storage costs the restore, not the run.
  }
}

let wiederhergestellt = false;

export async function stelleWiederHer(): Promise<void> {
  if (wiederhergestellt || zustand.zuege.length > 0) return;
  wiederhergestellt = true;

  let abgelegt: { konversationId: string | null; zuege: AbgelegterZug[] };
  try {
    const roh = sessionStorage.getItem(ABLAGE);
    if (!roh) return;
    abgelegt = JSON.parse(roh) as typeof abgelegt;
  } catch {
    return;
  }

  if (!Array.isArray(abgelegt.zuege) || abgelegt.zuege.length === 0) return;

  setze((alt) => ({
    ...alt,
    konversationId: abgelegt.konversationId,
    zuege: abgelegt.zuege.map((z) => ({
      id: z.id,
      frage: z.frage,
      runId: z.runId,
      ereignisse: [],
      teiltexte: {},
      status: "laeuft" as LaufStatus,
      antwortMd: null,
      fehlerCode: null,
      fehlerText: null,
      lauf: null,
      kontextLabel: z.kontextLabel,
    })),
  }));

  for (const zug of abgelegt.zuege) {
    if (!zug.runId) continue;
    const status = await ladeLauf(zug.id, zug.runId);
    // The newest run may still be in flight; reattach rather than freeze it.
    if (status === "laeuft" || status === "geplant") void beobachte(zug.id, zug.runId);
  }
}

// ── Werkzeugkatalog ─────────────────────────────────────────────────────────

let katalogGeladen = false;

/** The tool catalogue, fetched once. Used to name and explain each step. */
export async function ladeKatalog(): Promise<void> {
  if (katalogGeladen) return;
  katalogGeladen = true;
  try {
    const antwort = await get<{ zeilen: WerkzeugEintrag[] }>("/api/assistent/werkzeuge");
    const karte = new Map(antwort.zeilen.map((z) => [z.name, z]));
    setze((alt) => ({ ...alt, katalog: karte }));
  } catch {
    // A missing catalogue costs a label, not a feature. The trace falls back to the
    // tool's own identifier, so failing quietly here is the correct behaviour.
    katalogGeladen = false;
  }
}

// ── Eine Frage stellen ──────────────────────────────────────────────────────

export interface FrageKontext {
  liegenschaftId: string | null;
  periode: string | null;
  label: string | null;
  /** Everything the panel knows about the current screen. Ignored by the API today. */
  kontext: Record<string, unknown>;
}

let laufAbbrecher: AbortController | null = null;

export async function sendeFrage(text: string, kontext: FrageKontext): Promise<void> {
  const zugId = `z${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

  setze((alt) => ({
    ...alt,
    sendet: true,
    zuege: [
      ...alt.zuege,
      {
        id: zugId,
        frage: text,
        runId: null,
        ereignisse: [],
        teiltexte: {},
        status: "geplant",
        antwortMd: null,
        fehlerCode: null,
        fehlerText: null,
        lauf: null,
        kontextLabel: kontext.label,
      },
    ],
  }));

  let runId: string;
  try {
    const antwort = await post<{ runId: string; konversationId: string }>(
      "/api/assistent/nachricht",
      {
        text,
        konversationId: zustand.konversationId,
        liegenschaftId: kontext.liegenschaftId,
        periode: kontext.periode,
        kontext: kontext.kontext,
      },
    );
    runId = antwort.runId;
    setze((alt) => ({ ...alt, sendet: false, konversationId: antwort.konversationId }));
  } catch (fehler) {
    aendereZug(zugId, (z) => ({
      ...z,
      status: "fehlgeschlagen",
      fehlerCode: "anfrage",
      fehlerText: (fehler as Error).message,
    }));
    setze((alt) => ({ ...alt, sendet: false }));
    return;
  }

  aendereZug(zugId, (z) => ({ ...z, runId, status: "laeuft" }));
  await beobachte(zugId, runId);
}

/** Consecutive transport failures tolerated before the panel says the line is down. */
const MAX_FEHLVERSUCHE = 5;

/**
 * Watches one run to completion.
 *
 * The stream is the fast path and the run row is the truth. Each time the stream ends,
 * the row is read — it carries the final text, the cost, and the failure in a field the
 * stream never sends. Without that read a failed run shows an empty bubble, which is
 * the state this panel was found in.
 *
 * And it reattaches. A run that delegates spends minutes in a single tool call, long
 * enough for a proxy, a laptop lid or a dev-server reload to drop the connection; a
 * panel that gave up there would leave a run that is still working looking like a run
 * that hung.
 *
 * Each attach names the highest sequence this tab already holds, so the server resumes
 * at the tail rather than re-sending the run from its first event. The merge by `seq`
 * stays — it is what makes an overlap harmless — but it is now the safety net rather
 * than the mechanism, and a long run that reconnects six times no longer re-sends its
 * whole timeline six times.
 */
async function beobachte(zugId: string, runId: string): Promise<void> {
  laufAbbrecher?.abort();
  const abbrecher = new AbortController();
  laufAbbrecher = abbrecher;

  let fehlversuche = 0;

  for (;;) {
    try {
      await verfolgeStrom(`/api/assistent/laeufe/${runId}/strom?seit=${hoechsteSeq(zugId)}`, {
        signal: abbrecher.signal,
        aufRahmen: (rahmen) => {
          if (rahmen.art === "ereignis") {
            const ereignis = rahmen.daten as RohEreignis;
            aendereZug(zugId, (z) => ({
              ...z,
              ereignisse: einsortieren(z.ereignisse, ereignis),
              // A durable event can only be written after the model call that produced
              // the partial text finished, so any buffered text is now superseded.
              teiltexte: {},
            }));
          } else if (rahmen.art === "teiltext") {
            const rohRahmen = rahmen.daten as { block?: number; text?: string };
            if (typeof rohRahmen.text !== "string") return;
            const block = rohRahmen.block ?? 0;
            const text = rohRahmen.text;
            aendereZug(zugId, (z) => ({ ...z, teiltexte: { ...z.teiltexte, [block]: text } }));
          }
        },
      });
      fehlversuche = 0;
    } catch (fehler) {
      if (abbrecher.signal.aborted) return;
      fehlversuche += 1;
      if (fehlversuche >= MAX_FEHLVERSUCHE) {
        aendereZug(zugId, (z) => ({
          ...z,
          status: "fehlgeschlagen",
          fehlerCode: "verbindung",
          fehlerText: (fehler as Error).message,
        }));
        return;
      }
    }

    if (abbrecher.signal.aborted) return;

    const status = await ladeLauf(zugId, runId);
    if (status === null) {
      // The row could not be read. That is a statement about the connection, not
      // about the run, so it counts towards the same patience budget.
      fehlversuche += 1;
      if (fehlversuche >= MAX_FEHLVERSUCHE) {
        aendereZug(zugId, (z) => ({
          ...z,
          status: "fehlgeschlagen",
          fehlerCode: "verbindung",
          fehlerText: "Der Lauf ist nicht mehr erreichbar. Die Verbindung zum Server ist gestört.",
        }));
        return;
      }
    } else if (status !== "laeuft" && status !== "geplant") {
      return;
    }

    await schlafe(500);
    if (abbrecher.signal.aborted) return;
  }
}

interface LaufAntwort {
  lauf: LaufZeile;
  ereignisse: RohEreignis[];
}

/**
 * Reads the authoritative run row and reconciles the transcript against it.
 *
 * Returns the run's status so the watcher knows whether to reattach. A failed read is
 * not treated as a failed run — the row is polled again on the next pass, because a
 * dev-server restart or a momentary 502 says nothing about the work in Postgres.
 */
async function ladeLauf(zugId: string, runId: string): Promise<LaufStatus | null> {
  try {
    const antwort = await get<LaufAntwort>(`/api/assistent/laeufe/${runId}`);
    aendereZug(zugId, (z) => ({
      ...z,
      lauf: antwort.lauf,
      status: antwort.lauf.status,
      antwortMd: antwort.lauf.ergebnisMd,
      fehlerCode: antwort.lauf.fehlerCode,
      fehlerText: antwort.lauf.fehlerText,
      ereignisse:
        antwort.ereignisse.length >= z.ereignisse.length ? antwort.ereignisse : z.ereignisse,
      teiltexte: {},
    }));

    if (antwort.lauf.status === "wartet_auf_freigabe") await ladeFreigaben();
    return antwort.lauf.status;
  } catch {
    return null;
  }
}

/**
 * The replay watermark for this turn: what the panel has already drawn.
 *
 * Read at attach time rather than tracked in a variable, because `ladeLauf` replaces
 * the event list wholesale between attempts and a counter would go stale exactly when
 * it is used.
 */
function hoechsteSeq(zugId: string): number {
  const zug = zustand.zuege.find((z) => z.id === zugId);
  if (!zug || zug.ereignisse.length === 0) return 0;
  return zug.ereignisse.reduce((hoechste, e) => Math.max(hoechste, e.seq), 0);
}

/** The overlap is bounded now, not gone: a frame in flight when we reattach arrives twice. */
function einsortieren(vorhanden: RohEreignis[], neu: RohEreignis): RohEreignis[] {
  if (vorhanden.some((e) => e.seq === neu.seq)) return vorhanden;
  return [...vorhanden, neu].sort((a, b) => a.seq - b.seq);
}

// ── Freigaben ───────────────────────────────────────────────────────────────

export async function ladeFreigaben(): Promise<void> {
  try {
    const antwort = await get<{ zeilen: OffeneFreigabe[] }>("/api/freigaben");
    const karte: Record<string, OffeneFreigabe> = {};
    for (const zeile of antwort.zeilen) karte[zeile.id] = zeile;
    setze((alt) => ({ ...alt, freigaben: { ...alt.freigaben, ...karte } }));
  } catch {
    // The card falls back to the checkpoint's own title, which is already German and
    // already says what the step was. Better than replacing the card with an error.
  }
}

/**
 * Signs or refuses, then picks the run back up.
 *
 * The resume is watched rather than assumed. The API restarts the loop after the last
 * required signature, but the status flip is not synchronous with the response, so the
 * run is polled briefly before the stream is reattached — reconnecting too early would
 * be answered with the run's previous terminal frame and the panel would sit still
 * while work was happening.
 */
export async function entscheideFreigabe(
  freigabeId: string,
  entscheidung: "erteilt" | "verweigert",
  notiz: string | null,
): Promise<void> {
  setzeFreigabeZustand(freigabeId, { laeuft: true, entschieden: null, fehler: null });

  let ergebnis: { status: string; runId: string | null };
  try {
    ergebnis = await post<{ status: string; runId: string | null }>(
      `/api/freigaben/${freigabeId}`,
      { entscheidung, notiz },
    );
  } catch (fehler) {
    setzeFreigabeZustand(freigabeId, {
      laeuft: false,
      entschieden: null,
      fehler: (fehler as Error).message,
    });
    return;
  }

  setzeFreigabeZustand(freigabeId, { laeuft: false, entschieden: entscheidung, fehler: null });
  await ladeFreigaben();

  if (ergebnis.status === "offen" || !ergebnis.runId) return;

  const runId = ergebnis.runId;
  const zug = zustand.zuege.find((z) => z.runId === runId);
  if (!zug) return;

  aendereZug(zug.id, (z) => ({ ...z, status: "laeuft" }));

  const wieder = await wartetAufFortsetzung(runId);
  if (wieder) await beobachte(zug.id, runId);
  else await ladeLauf(zug.id, runId);
}

/** Polls the run until the resume has taken effect. Gives up after about four seconds. */
async function wartetAufFortsetzung(runId: string): Promise<boolean> {
  for (let versuch = 0; versuch < 10; versuch += 1) {
    await schlafe(400);
    try {
      const antwort = await get<LaufAntwort>(`/api/assistent/laeufe/${runId}`);
      if (antwort.lauf.status !== "wartet_auf_freigabe") return antwort.lauf.status === "laeuft";
    } catch {
      return false;
    }
  }
  return false;
}

function schlafe(ms: number): Promise<void> {
  return new Promise((fertig) => setTimeout(fertig, ms));
}

function setzeFreigabeZustand(freigabeId: string, wert: FreigabeZustand): void {
  setze((alt) => ({
    ...alt,
    freigabeZustand: { ...alt.freigabeZustand, [freigabeId]: wert },
  }));
}

// ── Gespräch ────────────────────────────────────────────────────────────────

export function neuesGespraech(): void {
  laufAbbrecher?.abort();
  laufAbbrecher = null;
  setze((alt) => ({
    ...LEER,
    katalog: alt.katalog,
  }));
}

/** Total spend of the conversation so far, in cents. */
export function gesamtkostenCent(sitzung: Sitzung): number {
  return sitzung.zuege.reduce((summe, zug) => summe + (zug.lauf?.verbrauchCent ?? 0), 0);
}
